import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import Cookies from "js-cookie";
import {
  Form,
  Input,
  InputNumber,
  Button,
  Upload,
  message,
  notification,
  Spin,
  Divider
} from "antd";
import {
  HiOutlineChevronLeft,
  HiOutlineClock,
  HiOutlineCheck,
  HiOutlinePhotograph
} from 'react-icons/hi';
import "./AddService.css";

const CustomSwitch = ({ checked, onChange }) => (
  <div
    onClick={() => onChange && onChange(!checked)}
    className={`w-8 h-4.5 flex items-center rounded-full p-0.5 cursor-pointer transition-colors duration-200 shrink-0 select-none ${
      checked ? 'bg-[#009688]' : 'bg-gray-300'
    }`}
  >
    <div
      className={`bg-white w-3.5 h-3.5 rounded-full shadow-xs transform transition-transform duration-200 ${
        checked ? 'translate-x-3.5' : 'translate-x-0'
      }`}
    />
  </div>
);

const ManageAddServiceComponent = () => {
  const history = useHistory();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState("");

  useEffect(() => {
    fetchSpeciality();
  }, []);

  const fetchSpeciality = async () => {
    try {
      const response = await axiosInstance.post("doctor/getSpecialitybyDoctor", {
        doctor_id: Cookies.get("Doctor_id") || Cookies.get("doctor_id") || localStorage.getItem("Doctor_id")
      });
      if (response.data.status === 1) {
        if (response.data.data?.length > 0) {
          form.setFieldsValue({
            specialityId: response.data.data[0].id || response.data.data[0].specialityId,
            speciality_name: response.data.data[0].speciality || response.data.data[0].speciality_name
          });
        }
      }
    } catch (err) {
      console.error("Failed to fetch speciality", err);
    }
  };

  const handleCustomUpload = async ({ file, onSuccess, onError }) => {
    setUploading(true);
    const formData = new FormData();
    formData.append("module_id", "3");
    formData.append("file", file);
    formData.append("pathLocation", "MANAGE-SERVICE/");

    try {
      const response = await axiosInstance.post("doctor/awsS3FileUpload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      const location = response.data?.url || response.data?.privatePathUrl || response.data?.encryptedData?.url || response.data?.encryptedData?.privatePathUrl || response.data?.filepath?.Location || response.data?.data?.Location;
      if (location) {
        setImageUrl(location.split('?')[0]);
        message.success("Image uploaded successfully");
        onSuccess("ok");
      } else {
        message.error("Image upload failed");
        onError("Upload failed");
      }
    } catch (err) {
      message.error("Image upload failed");
      onError(err);
    } finally {
      setUploading(false);
    }
  };

  const onFinish = async (values) => {
    if (values.cost <= 1) {
      notification.warning({ message: `The fee should be above ₹ 1.` });
      return;
    }
    if (values.duration < 5 || values.duration > 120) {
      notification.warning({ message: "Slot Duration must be between 5 and 120 minutes" });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        id: 0,
        doctor_id: Cookies.get("Doctor_id") || Cookies.get("doctor_id") || localStorage.getItem("Doctor_id"),
        service: values.service,
        specialityId: values.specialityId,
        cost: values.cost,
        slot_duration: `00:${values.duration}`,
        description: values.description,
        is_active: values.is_active ? 1 : 0,
        file_name: "service_image",
        file_path: imageUrl,
      };

      const response = await axiosInstance.post("doctor/addmanageservice", payload);
      if (response.data.status === 1 || response.data.data?.[0]?.ErrorCode === '9999') {
        notification.success({
          message: "Success!",
          description: "New medical service has been added successfully."
        });
        history.push("/dashboard/services");
      } else {
        const errorMsg = response.data.msg || response.data.data?.[0]?.msg || "Failed to add service";
        notification.error({ message: errorMsg });
      }
    } catch (err) {
      console.error("Add service error", err);
      notification.error({ message: "An error occurred" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full space-y-2.5 animate-in fade-in duration-300 text-gray-800">
      {/* Full-Width Compact Card Container */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-2xs overflow-hidden w-full">
        {/* Sleek Integrated Header Bar */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-white">
          <div className="flex items-center gap-2.5">
            <button
              onClick={() => history.goBack()}
              className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600 transition-colors border-none bg-transparent cursor-pointer flex items-center justify-center"
              title="Back"
            >
              <HiOutlineChevronLeft size={18} />
            </button>
            <div>
              <h2 className="text-sm font-bold text-gray-800 leading-tight m-0">Add New Service</h2>
              <p className="text-gray-400 text-[11px] m-0 font-medium">Create a new service offering for your clinic</p>
            </div>
          </div>
        </div>

        {/* Compact Form Body */}
        <div className="p-3.5">
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{ is_active: true, duration: 15 }}
            size="small"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5 mb-2.5">
              <Form.Item
                label={<span className="text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-0.5">Speciality</span>}
                name="speciality_name"
                className="mb-0"
              >
                <Input disabled size="small" className="bg-gray-50 rounded-md font-semibold text-gray-700 text-xs h-8" placeholder="Speciality" />
              </Form.Item>

              <Form.Item name="specialityId" hidden><Input /></Form.Item>

              <Form.Item
                label={<span className="text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-0.5">Service Title</span>}
                name="service"
                rules={[{ required: true, message: 'Please enter service title' }]}
                className="mb-0"
              >
                <Input size="small" placeholder="e.g. General Consultation" className="rounded-md text-xs h-8" />
              </Form.Item>

              <Form.Item
                label={<span className="text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-0.5">Consultation Fee (₹)</span>}
                name="cost"
                rules={[{ required: true, message: 'Please enter fee' }]}
                className="mb-0"
              >
                <InputNumber
                  min={1}
                  size="small"
                  className="w-full rounded-md text-xs h-8 flex items-center"
                  prefix={<span className="text-gray-500 font-bold mr-1">₹</span>}
                  placeholder="0.00"
                />
              </Form.Item>

              <Form.Item
                label={<span className="text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-0.5">Duration (Minutes)</span>}
                name="duration"
                rules={[{ required: true, message: 'Please enter duration' }]}
                className="mb-0"
              >
                <InputNumber
                  min={5}
                  max={120}
                  size="small"
                  className="w-full rounded-md text-xs h-8 flex items-center"
                  prefix={<HiOutlineClock className="text-gray-400 mr-1" size={14} />}
                  placeholder="15"
                />
              </Form.Item>

              <div>
                <label className="text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-0.5 block">
                  Active Status
                </label>
                <div className="flex items-center gap-2.5 h-8 bg-gray-50 px-2.5 rounded-md border border-gray-200">
                  <Form.Item name="is_active" valuePropName="checked" noStyle>
                    <CustomSwitch />
                  </Form.Item>
                  <span className="text-[11px] text-gray-600 font-medium">
                    Visible to patients when active
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 mb-2">
              <div className="md:col-span-2">
                <Form.Item
                  label={<span className="text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-0.5">Service Description</span>}
                  name="description"
                  rules={[{ required: true, message: 'Please enter description' }]}
                  className="mb-0"
                >
                  <Input.TextArea
                    rows={2}
                    placeholder="Describe the medical service..."
                    className="rounded-md text-xs p-2"
                  />
                </Form.Item>
              </div>

              <div>
                <Form.Item label={<span className="text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-0.5">Service Image</span>} className="mb-0">
                  <Upload
                    listType="picture-card"
                    showUploadList={false}
                    customRequest={handleCustomUpload}
                    className="w-full"
                  >
                    {imageUrl ? (
                      <div className="relative w-full h-16 rounded-md overflow-hidden group">
                        <img src={imageUrl} alt="service" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-[10px] font-bold">
                          Change
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center p-1 text-gray-400">
                        {uploading ? <Spin size="small" /> : <HiOutlinePhotograph size={18} className="text-teal-600 mb-0.5" />}
                        <span className="text-[10px] font-semibold text-gray-600">Upload Image</span>
                        <span className="text-[9px] text-gray-400">PNG, JPG up to 5MB</span>
                      </div>
                    )}
                  </Upload>
                </Form.Item>
              </div>
            </div>

            <Divider className="my-2" />

            {/* Bottom Actions Bar */}
            <div className="flex items-center justify-end gap-2">
              <Button
                size="small"
                className="rounded-md px-3.5 h-8 font-bold text-xs border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => history.goBack()}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                size="small"
                loading={loading}
                className="bg-[#009688] hover:bg-[#00897b] text-white rounded-md px-4 h-8 font-bold text-xs border-none shadow-2xs cursor-pointer flex items-center gap-1"
              >
                <HiOutlineCheck size={15} />
                Save Service
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default ManageAddServiceComponent;
